/**
 * Auth client: session-token storage + the public auth and admin API calls.
 *
 * The session token is an opaque string issued by the orchestrator on login.
 * We persist it in localStorage and attach it as a Bearer header to every API
 * request (see `authHeaders` / `authedFetch` in ./api).
 */

// API routes live under /api (see src/lib/api.ts for the rationale).
const API_ORIGIN =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/$/, '')) || '';
const BASE = `${API_ORIGIN}/api`;

const TOKEN_KEY = 'af.session.token';

/**
 * Tenancy roles. Legacy ('admin' / 'internal') values are accepted by the
 * server during the migration window but new responses always use the new
 * names.
 */
export type UserRole =
  | 'super_admin'
  | 'enterprise_admin'
  | 'enterprise_user'
  | 'admin' // legacy
  | 'internal'; // legacy

/** True for any role that should see the admin nav + member management. */
export function isAdminRole(role: UserRole): boolean {
  return role === 'super_admin' || role === 'enterprise_admin' || role === 'admin';
}

export function isSuperAdminRole(role: UserRole): boolean {
  return role === 'super_admin';
}

export interface AuthEnterprise {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  /** Showcase workspaces enable the guided tour / simulation / build-demo. */
  isShowcase?: boolean;
}

export type WorkspaceRole = 'enterprise_admin' | 'enterprise_user';

export interface Membership {
  enterprise: AuthEnterprise;
  role: WorkspaceRole;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  isActive: boolean;
  enterpriseId: string | null;
  hasPassword: boolean;
  createdAt: string;
}

/* ---------- token storage ---------- */

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Absorb a session token injected via the URL fragment, e.g.
 * `https://app.example.com/#session=<token>`. This is how the embedded Forge
 * Jira page signs the user in silently: its resolver mints a session from the
 * user's verified Atlassian identity and hands the SPA the token here, so no
 * login screen is shown. The fragment is stripped immediately afterwards so the
 * token never lingers in the address bar, history, or a copied link. Runs once,
 * before first render. Returns true when a token was absorbed.
 */
export function absorbSessionFromUrl(): boolean {
  try {
    const hash = window.location.hash;
    const match = /(?:^#|&)session=([^&]+)/.exec(hash);
    if (!match) return false;
    setToken(decodeURIComponent(match[1]));
    // Strip only the session param, preserving any other fragment/route.
    const cleaned = hash.replace(/(?:^#|&)session=[^&]+/, (m) => (m.startsWith('#') ? '#' : ''));
    const newHash = cleaned === '#' ? '' : cleaned;
    window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    return true;
  } catch {
    return false;
  }
}

/** Thrown by auth calls so the UI can show a precise message. */
export class AuthError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

function authHeaders(json = false): Record<string, string> {
  const token = getToken();
  const h: Record<string, string> = {};
  if (json) h['Content-Type'] = 'application/json';
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: authHeaders(body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AuthError(
      (json as any).message || (json as any).error || `Request failed (${res.status})`,
      (json as any).error || 'error',
      res.status,
    );
  }
  return json as T;
}

/* ---------- public auth endpoints ---------- */

/**
 * Self-register against an EXISTING workspace. The user lands inactive — a
 * workspace admin must approve them before they can sign in. Resolves to
 * the workspace name on success so the UI can confirm what the user joined.
 *
 * NOTE: No session token is returned (the user isn't logged in yet).
 */
export async function register(
  email: string,
  password: string,
  opts: { name?: string; workspace: string },
): Promise<{ workspaceName: string; message: string }> {
  return req<{ workspaceName: string; message: string }>('POST', '/auth/register', {
    email,
    password,
    name: opts.name,
    workspace: opts.workspace,
  });
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const { token, user } = await req<{ token: string; user: AuthUser }>('POST', '/auth/login', {
    email,
    password,
  });
  setToken(token);
  return user;
}

/**
 * Accept an invitation. For a brand-new email this creates an account and
 * returns a logged-in session. For an EXISTING account it just adds a
 * membership (no session) — the caller should send them to sign in.
 */
export async function acceptInvite(
  token: string,
  password: string,
  name?: string,
): Promise<
  | { joined: true; user: AuthUser; enterprise: AuthEnterprise }
  | { joined: false; enterprise: AuthEnterprise; message: string }
> {
  const resp = await req<{
    token?: string;
    user?: AuthUser;
    joined?: boolean;
    message?: string;
    enterprise: { id: string; name: string; slug: string };
  }>('POST', '/auth/accept-invite', { token, password, name });
  const enterprise: AuthEnterprise = { ...resp.enterprise, isActive: true };
  // Existing account → membership added, no session minted.
  if (resp.joined && !resp.token) {
    return { joined: false, enterprise, message: resp.message ?? 'Added to workspace. Please sign in.' };
  }
  setToken(resp.token!);
  return { joined: true, user: resp.user!, enterprise };
}

export async function logout(): Promise<void> {
  const token = getToken();
  if (token) {
    try {
      await fetch(`${BASE}/auth/logout`, { method: 'POST', headers: authHeaders() });
    } catch {
      /* ignore */
    }
  }
  clearToken();
}

export interface MeResponse {
  user: AuthUser;
  memberships: Membership[];
  activeEnterpriseId: string | null;
  activeEnterprise: AuthEnterprise | null;
  /** Effective per-workspace role of the caller in the active workspace. */
  activeRole: WorkspaceRole | null;
  /** Present for super-admins: every workspace they may switch into. */
  allEnterprises?: AuthEnterprise[];
}

export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch(`${BASE}/auth/me`, { headers: authHeaders() });
  if (!res.ok) throw new AuthError('not authenticated', 'unauthorized', res.status);
  return (await res.json()) as MeResponse;
}

/** Switch the active workspace for the current session. */
export async function switchWorkspace(enterpriseId: string): Promise<{
  activeEnterpriseId: string;
  activeEnterprise: AuthEnterprise;
  activeRole: WorkspaceRole | null;
}> {
  return req('POST', '/auth/switch-workspace', { enterpriseId });
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return req('POST', '/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return req('POST', '/auth/reset-password', { token, password });
}

/* ---------- admin: user management ---------- */

export async function listUsers(): Promise<AuthUser[]> {
  return (await req<{ users: AuthUser[] }>('GET', '/admin/users')).users;
}

export async function patchUser(id: string, fields: { role?: UserRole; isActive?: boolean }): Promise<AuthUser> {
  return (await req<{ user: AuthUser }>('PATCH', `/admin/users/${id}`, fields)).user;
}

export async function resetUserPassword(id: string, password: string): Promise<void> {
  await req('POST', `/admin/users/${id}/reset-password`, { password });
}

export async function deleteUser(id: string): Promise<void> {
  await req('DELETE', `/admin/users/${id}`);
}

/* ---------- admin: workspace settings ---------- */

export interface WorkspaceInfo {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
}

export async function getWorkspace(): Promise<WorkspaceInfo> {
  return (await req<{ workspace: WorkspaceInfo }>('GET', '/admin/workspace')).workspace;
}

export async function renameWorkspace(name: string): Promise<WorkspaceInfo> {
  return (await req<{ workspace: WorkspaceInfo }>('PATCH', '/admin/workspace', { name })).workspace;
}

/* ---------- admin: invitations ---------- */

export interface InvitationRow {
  id: string;
  email: string;
  role: 'enterprise_admin' | 'enterprise_user';
  createdAt: string;
  expiresAt: string;
}

export async function createInvitation(
  email: string,
  role: 'enterprise_admin' | 'enterprise_user',
  ttlDays?: number,
): Promise<{ invitation: InvitationRow; token: string }> {
  return req('POST', '/admin/invitations', { email, role, ttlDays });
}

export async function listInvitations(): Promise<InvitationRow[]> {
  return (await req<{ invitations: InvitationRow[] }>('GET', '/admin/invitations')).invitations;
}

export async function revokeInvitation(id: string): Promise<void> {
  await req('DELETE', `/admin/invitations/${id}`);
}

/* ---------- super-admin ---------- */

export interface EnterpriseSummary {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  dbPath: string;
  createdAt: string;
  memberCount: number;
  activeMemberCount: number;
}

export async function listEnterprises(): Promise<EnterpriseSummary[]> {
  return (await req<{ enterprises: EnterpriseSummary[] }>('GET', '/super-admin/enterprises')).enterprises;
}

export async function createEnterprise(input: {
  name: string;
  adminEmail: string;
  adminName?: string;
  slug?: string;
}): Promise<{ enterprise: EnterpriseSummary; admin: { userId: string; email: string; oneTimePassword?: string } }> {
  return req('POST', '/super-admin/enterprises', input);
}

export async function updateEnterprise(
  id: string,
  fields: { isActive?: boolean; name?: string },
): Promise<{ enterprise: EnterpriseSummary }> {
  return req('PATCH', `/super-admin/enterprises/${id}`, fields);
}

export async function deleteEnterprise(id: string): Promise<void> {
  await req('DELETE', `/super-admin/enterprises/${id}`);
}

export interface AuditLogEntry {
  id: number;
  actorUserId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: unknown;
  createdAt: string;
}

export async function listAuditLog(limit = 200): Promise<AuditLogEntry[]> {
  return (await req<{ entries: AuditLogEntry[] }>('GET', `/super-admin/audit-log?limit=${limit}`)).entries;
}

/* ---------- admin: config / settings ---------- */

export type ConfigScope = 'workspace' | 'platform';

export interface ConfigValue {
  value: string;
  isSet: boolean;
  sensitive: boolean;
  source: 'db' | 'env' | 'none';
  scope: ConfigScope;
}
export type ConfigMap = Record<string, ConfigValue>;

export interface AdminConfigResponse {
  config: ConfigMap;
  /** Caller's role — drives which tabs/fields the UI exposes. */
  role: UserRole;
}

export async function getAdminConfig(): Promise<AdminConfigResponse> {
  return req<AdminConfigResponse>('GET', '/admin/config');
}

export async function saveAdminConfig(values: Record<string, string>): Promise<AdminConfigResponse> {
  return req<AdminConfigResponse>('PUT', '/admin/config', { values });
}

export async function testLLM(
  provider: string,
  opts?: { message?: string; apiKey?: string; model?: string; baseUrl?: string },
): Promise<{ ok: boolean; model?: string; response?: string; error?: string }> {
  return req('POST', '/admin/config/test-llm', { provider, ...opts });
}

/** Models offered by a self-hosted provider's server (Ollama). Blank fields use the saved config. */
export async function listLlmModels(
  provider: 'ollama',
  opts?: { baseUrl?: string; apiKey?: string },
): Promise<{ ok: boolean; models?: string[]; error?: string }> {
  return req('POST', '/admin/config/llm-models', { provider, ...opts });
}

/* ----------------------- Confluence connection profiles ----------------------- */

export interface ConfluenceProfile {
  id: string;
  name: string;
  siteUrl: string;
  userEmail: string;
  confluenceSpace: string;
  jiraProject: string;
  isDefault: boolean;
  hasToken: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConfluenceProfileInput {
  name: string;
  siteUrl?: string;
  userEmail?: string;
  /** Plaintext token; omit/blank on edit to keep the stored one. */
  apiToken?: string;
  confluenceSpace?: string;
  jiraProject?: string;
  isDefault?: boolean;
}

export async function listConfluenceProfiles(): Promise<ConfluenceProfile[]> {
  const r = await req<{ profiles: ConfluenceProfile[] }>('GET', '/admin/confluence-profiles');
  return r.profiles;
}

export async function createConfluenceProfile(input: ConfluenceProfileInput): Promise<ConfluenceProfile> {
  const r = await req<{ profile: ConfluenceProfile }>('POST', '/admin/confluence-profiles', input);
  return r.profile;
}

export async function updateConfluenceProfile(
  id: string,
  patch: Partial<ConfluenceProfileInput>,
): Promise<ConfluenceProfile> {
  const r = await req<{ profile: ConfluenceProfile }>('PUT', `/admin/confluence-profiles/${id}`, patch);
  return r.profile;
}

export async function deleteConfluenceProfile(id: string): Promise<void> {
  await req('DELETE', `/admin/confluence-profiles/${id}`);
}

export async function setDefaultConfluenceProfile(id: string): Promise<ConfluenceProfile[]> {
  const r = await req<{ profiles: ConfluenceProfile[] }>('POST', `/admin/confluence-profiles/${id}/default`);
  return r.profiles;
}

export async function testConfluenceProfile(input: {
  id?: string;
  siteUrl?: string;
  userEmail?: string;
  apiToken?: string;
}): Promise<{ ok: boolean; message: string }> {
  return req('POST', '/admin/confluence-profiles/test', input);
}

export async function testSmtp(): Promise<{ ok: boolean; message: string }> {
  return req('POST', '/admin/config/test-smtp');
}

export async function testDcConnection(input: {
  JIRA_DC_URL?: string;
  JIRA_DC_PAT?: string;
}): Promise<{ ok: boolean; message: string; version?: string }> {
  return req('POST', '/admin/config/test-dc', input);
}

/* ----------------------- Git host connection profiles ----------------------- */

export type RepoHost = 'github' | 'bitbucket';

export interface RepoProfile {
  id: string;
  name: string;
  host: RepoHost;
  baseUrl: string;
  workspace: string;
  repo: string;
  defaultBranch: string;
  syncBranch: string;
  authKind: 'pat' | 'oauth';
  username: string;
  hasToken: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RepoProfileInput {
  name: string;
  host: RepoHost;
  baseUrl?: string;
  workspace?: string;
  repo?: string;
  defaultBranch?: string;
  syncBranch?: string;
  username?: string;
  /** Plaintext token; omit/blank on edit to keep the stored one. */
  authToken?: string;
  isDefault?: boolean;
}

export async function listRepoProfiles(): Promise<RepoProfile[]> {
  const r = await req<{ profiles: RepoProfile[] }>('GET', '/admin/repo-profiles');
  return r.profiles;
}

export async function createRepoProfile(input: RepoProfileInput): Promise<RepoProfile> {
  const r = await req<{ profile: RepoProfile }>('POST', '/admin/repo-profiles', input);
  return r.profile;
}

export async function updateRepoProfile(
  id: string,
  patch: Partial<RepoProfileInput>,
): Promise<RepoProfile> {
  const r = await req<{ profile: RepoProfile }>('PUT', `/admin/repo-profiles/${id}`, patch);
  return r.profile;
}

export async function deleteRepoProfile(id: string): Promise<void> {
  await req('DELETE', `/admin/repo-profiles/${id}`);
}

export async function setDefaultRepoProfile(id: string): Promise<RepoProfile[]> {
  const r = await req<{ profiles: RepoProfile[] }>('POST', `/admin/repo-profiles/${id}/default`);
  return r.profiles;
}

export async function testRepoProfile(input: {
  id?: string;
  host?: RepoHost;
  baseUrl?: string;
  workspace?: string;
  repo?: string;
  username?: string;
  authToken?: string;
}): Promise<{ ok: boolean; message: string }> {
  return req('POST', '/admin/repo-profiles/test', input);
}
